import axios from "axios";

const API_URL = process.env.REACT_APP_BASE_URL;

if (!API_URL) {
  console.warn("REACT_APP_BASE_URL is not defined in .env file.");
} else {
}

/** Same shape as Bearer attachment — use in hooks (e.g. useProfile) for consistency. */
export function getStoredAccessToken(): string | undefined {
  try {
    const user_data = localStorage.getItem("authUser");
    if (!user_data) return undefined;
    const parsed = JSON.parse(user_data);
    return parsed?.access || parsed?.token || parsed?.auth_token;
  } catch {
    return undefined;
  }
}

function clearAuthStorage() {
  localStorage.removeItem("authUser");
  localStorage.removeItem("userPermissions");
  localStorage.removeItem("authToken");
  localStorage.removeItem("permission_user");
}

/** Failed login/register should not wipe session — only 401 on authenticated calls. */
function isUnauthenticatedAuthRequest(url: string | undefined): boolean {
  if (!url) return false;
  const u = url.toLowerCase();
  return (
    u.includes("user/login") ||
    u.includes("user/register") ||
    u.includes("token/refresh")
  );
}

export const Axios = axios.create({
  baseURL: API_URL,
  // withCredentials: true, // Uncomment if using cookies/sessions
  headers: {
    "Content-Type": "application/json",
  },
});

Axios.interceptors.request.use(
  (request) => {
    try {
      const TOKEN = getStoredAccessToken();
      if (TOKEN) {
        request.headers.Authorization = `Bearer ${TOKEN}`;
      } else if (localStorage.getItem("authUser")) {
        console.warn("No access token found in authUser");
      }
      return request;
    } catch (err) {
      console.error("Failed to parse token from localStorage:", err);
      return request;
    }
  },
  (error) => Promise.reject(error)
);


/** Paths where 401 is expected (bad credentials) — do not wipe session */
const isPublicAuthPath = (url: string) =>
  /\/login\/?/i.test(url) ||
  /\/register\/?/i.test(url) ||
  /\/signup\/?/i.test(url) ||
  /\/password-reset\/?/i.test(url) ||
  /\/token\/refresh\/?/i.test(url);

// const clearAuthStorage = () => {
//   localStorage.removeItem("authUser");
//   localStorage.removeItem("userPermissions");
//   localStorage.removeItem("permission_user");
// };

Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log(error)
    if (error.response) {
      const status = error.response.status;
      const reqUrl = error.config?.url;
      const responseData = error.response?.data || {};
      const detailMessage = String(responseData?.detail || "").toLowerCase();
      const apiMessage = String(responseData?.message || "").toLowerCase();
      const apiCode = String(responseData?.code || "").toLowerCase();
      const nestedTokenMessage = Array.isArray(responseData?.messages)
        ? responseData.messages.some((entry: any) =>
            String(entry?.message || "").toLowerCase().includes("token")
          )
        : false;
      const hasTokenAuth403 =
        status === 403 &&
        (apiCode === "token_not_valid" ||
          detailMessage.includes("invalid token") ||
          detailMessage.includes("token expired") ||
          detailMessage.includes("authentication credentials were not provided") ||
          apiMessage.includes("invalid token") ||
          apiMessage.includes("authentication required") ||
          apiMessage.includes("token expired") ||
          nestedTokenMessage);

      if (status === 401 && !isUnauthenticatedAuthRequest(reqUrl)) {
        clearAuthStorage();
        window.location.assign("/login");
        return Promise.reject(error);
      }

      console.error(
        ` API error [${status}]:`,
        error.response.data
      );

      const new_status = error.response.status;
      const requestUrl = String(error.config?.url ?? "");
      // Backend returns e.g. {"detail":"Invalid token"} when JWT is expired / invalid
      const invalidTokenDetail =
        error.response?.data?.detail === "Invalid token";

      const shouldLogout =
        !isPublicAuthPath(requestUrl) &&
        (new_status === 401 ||
          (invalidTokenDetail && new_status === 403) ||
          hasTokenAuth403);

      if (shouldLogout) {
        clearAuthStorage();
        const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
        window.location.assign(`${window.location.origin}${base}/login`);
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
